import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { getCommentIdFromHashFragment } from '@omnea-app/fe-routing/HashProvider'

interface AutoScrollToCommentContextType {
  urlCommentId?: string
  hasScrolledToComment: boolean
  handleScrollToComment(): void
}

export const AutoScrollToCommentContext = createContext<AutoScrollToCommentContextType>({
  urlCommentId: undefined,
  hasScrolledToComment: false,
  handleScrollToComment: () => undefined,
})

export function useCommentToScrollTo(commentId: string) {
  const { urlCommentId, hasScrolledToComment, handleScrollToComment } = useContext(AutoScrollToCommentContext)

  const isUrlComment = commentId === urlCommentId
  const shouldScrollToComment = isUrlComment && !hasScrolledToComment

  const commentElementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (shouldScrollToComment && commentElementRef.current) {
      // Add a delay to ensure component is mounted and layout is complete
      // before attempting to scroll to the comment
      const timeoutId = setTimeout(() => {
        // First scroll with instant behavior to ensure position
        // This happens immediately
        commentElementRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' })

        // Then apply smooth scroll
        // Second scroll waits for the next frame
        requestAnimationFrame(() => {
          commentElementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          handleScrollToComment()
        })
      }, 300)

      return () => clearTimeout(timeoutId)
    }
  }, [shouldScrollToComment, handleScrollToComment])

  return { commentElementRef, isUrlComment }
}

// Hook to listen to changes in the commentId in the URL
// Used by things like the comment widget, that needs to automatically open to render the comment
export function useListenToUrlCommentChange(handleUrlCommentChange: (commentId: string) => void) {
  const { urlCommentId } = useContext(AutoScrollToCommentContext)

  const callbackRef = useRef(handleUrlCommentChange)
  callbackRef.current = handleUrlCommentChange

  useEffect(() => {
    const commentIdFromUrl = getCommentIdFromUrlHash()
    if (!urlCommentId || commentIdFromUrl !== urlCommentId) {
      return
    }
    callbackRef.current(urlCommentId)
  }, [urlCommentId])
}

export function useUrlComment() {
  return useContext(AutoScrollToCommentContext).urlCommentId
}

export function getCommentIdFromUrlHash(): string | undefined {
  if (typeof window === 'undefined') return undefined

  return getCommentIdFromHashFragment(window.location.hash.slice(1))
}

export const AutoScrollToCommentProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [urlCommentId, setUrlCommentId] = useState(getCommentIdFromUrlHash)
  const [hasScrolledToComment, setHasScrolledToComment] = useState(false)

  const handleScrollToComment = useCallback(() => {
    setHasScrolledToComment(true)
    // Clear so navigating to the same comment link again re-triggers the flow.
    setUrlCommentId(undefined)
  }, [])

  // listen for URL changes
  useEffect(() => {
    const handleHashChange = () => {
      const newUrlComment = getCommentIdFromUrlHash()
      // Only update when navigating to a comment hash. Non-comment hash changes (e.g. question
      // deep-links written by useScrollToQuestion) must not clear urlCommentId — that would
      // cancel the in-progress sidebar comment auto-scroll.
      if (newUrlComment !== undefined) {
        setUrlCommentId(newUrlComment)
        setHasScrolledToComment(false)
      }
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  return (
    <AutoScrollToCommentContext.Provider
      value={{
        urlCommentId,
        hasScrolledToComment,
        handleScrollToComment,
      }}
    >
      {children}
    </AutoScrollToCommentContext.Provider>
  )
}
